/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    shape, string, arrayOf, number, func,
    object,
    bool,
} from 'prop-types';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { useUIDSeed } from 'react-uid';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import YotpoProductReview from '../YotpoProductReview';
import ProductReview from '../ProductReview';
import { productType } from '../common/types';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.down(600)]: {
            padding: '0px 16px',
        },
    },
    container: {
        display: 'flex',
        alignItems: 'center',
    },
    productSku: {
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.5px',
        [theme.breakpoints.down(600)]: {
            fontSize: '14px',
        },
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 23.4%);',
        justifyContent: 'space-between',
        alignItems: 'center',
        gridAutoRows: '1fr',
        cursor: 'pointer',
    },
    pdpCount: {
        fontSize: '16px',
        lineHeight: '26px',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        margin: '16px 0px',
        color: '#434343',
        fontWeight: '800',
        [theme.breakpoints.down(600)]: {
            margin: '0px 0px 16px',
        },
    },
    wrapper: {
        background: '#F9F9FB',
        position: 'relative',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0px 4px',
        marginBottom: '12px',
        minHeight: '122px',
        border: '1px solid #F0F0F5',
        [theme.breakpoints.down(600)]: {
            minHeight: '86px',
        },
    },
    borderStyle: {
        border: '1px solid #201648',
    },
    productName: {
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.5px',
        marginBottom: '2px',
        [theme.breakpoints.down(600)]: {
            lineHeight: '18px',
            fontSize: '12px',
        },
    },
    productPrice: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column-reverse',
        '& span': {
            fontSize: '16px',
            lineHeight: '20px',
            letterSpacing: '0.5px',
            margin: '0px',
            [theme.breakpoints.down(600)]: {
                fontSize: '14px',
                lineHeight: '21px',
            },
        },
        '& s': {
            fontSize: '13px',
            lineHeight: '15px',
            color: '#555555',
            marginRight: '5px',
            [theme.breakpoints.down(600)]: {
                fontSize: '12px',
            },
        },
        '& strong': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
        },
    },

    bestValue: {
        position: 'absolute',
        bottom: '-8px',
        color: 'white',
        background: '#10686B',
        padding: '3px 9px',
        borderRadius: '3px',
        fontSize: '10px',
        lineHeight: '9px',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        [theme.breakpoints.down(600)]: {
            fontSize: '9px',
            lineHeight: '8px',
        },
    },
}));

const PDPSkuSelector = ({
    skuData,
    selectProduct,
    renderSkuPrice,
    selectedSku,
    dynamicPriceList,
    isPdpRedesignEnabled,
    product,
    productUrl,
}) => {
    const classes = useStyles();
    const seed = useUIDSeed();
    const dispatch = useDispatch();
    const isDesktopDevice = useMediaQuery('(min-width: 599px)');
    const ffIsPDPReviewsEnabled = useSelector(getFeatureFlag('is-pdp-reviews-enabled'));
    const ffIsPowerReviewEnabled = useSelector(getFeatureFlag('is-power-review-enabled'));
    const ffIsYotpoEnabled = useSelector(getFeatureFlag('is-pdp-yotpo-enabled'));

    const getCountValue = (attributes) => {
        const attributesData = typeof attributes === 'string' ? JSON?.parse(attributes) : attributes;
        return attributesData?.find?.((item) => item?.name === 'count')?.values?.[0]?.value || '';
    };
    return (
        <div className={classes.root}>
            { isDesktopDevice
                        && (
                            <div className={classes.container}>
                                {ffIsPDPReviewsEnabled && ffIsPowerReviewEnabled && (
                                    <ProductReview
                                        product={product}
                                        selectedSku={selectedSku}
                                        productUrl={productUrl} // comes off content from 2 levels up
                                        skuEnabledOnMobile
                                        isStandardPdp
                                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                                    />
                                )}
                                {ffIsPDPReviewsEnabled && ffIsYotpoEnabled && (
                                    <YotpoProductReview
                                        product={product}
                                        selectedSku={selectedSku}
                                        productUrl={productUrl} // comes off content from 2 levels up
                                        skuEnabledOnMobile
                                        isStandardPdp
                                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                                    />
                                )}
                            </div>
                        )}
            <div className={classes.pdpCount}>Count</div>
            <div className={classes.gridContainer}>
                {skuData.map((options, index) => {
                    /**
                      * Dynamic price call - price list handling updating the retail price and pass it in new list.
                    */
                    const pricesList = dynamicPriceList(options);
                    const isLastSku = index === skuData?.length - 1; // Check if it's the last SKU
                    return (
                        <div
                            key={seed(options)}
                            className={`${classes.wrapper} ${selectedSku?.id === options?.id ? classes.borderStyle : ''}`}
                            role="presentation"
                            onClick={() => {
                                selectProduct({
                                    target: {
                                        value: options.id,
                                    },
                                });
                                dispatch(trackEvent({
                                    eventCategory: 'pdp reskin',
                                    eventAction: 'sku selector',
                                    eventLabel: `${options?.name} - ${options?.id}` || '',
                                }));
                            }}
                        >
                            <span
                                className={classes.productName}
                            >
                                {options?.attributes && `${getCountValue(options.attributes)} ct.`}
                            </span>
                            <span className={classes.productPrice}>
                                <strong>
                                    {renderSkuPrice(options, false, pricesList)}
                                </strong>
                            </span>
                            {isLastSku  && (
                                <span className={classes.bestValue}>
                                    <strong>
                                        Best Value
                                    </strong>
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
PDPSkuSelector.propTypes = {
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    selectProduct: func.isRequired,
    renderSkuPrice: func,
    selectedSku: object.isRequired,
    dynamicPriceList: func.isRequired,
    productUrl: string.isRequired,
    product: productType.isRequired,
    isPdpRedesignEnabled: bool,
};
PDPSkuSelector.defaultProps = {
    renderSkuPrice: () => { },
    isPdpRedesignEnabled: false,
};
export default PDPSkuSelector;
